import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { NAV } from '../../navLabels';
import { PAGE_SIZE } from '../../constants';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../auth/authSlice';
import {
  useGetContractorQuery,
  useGetContractorAdminsQuery,
  useCreateContractorAdminMutation,
  useDeleteContractorAdminMutation,
  useGetInviteLinksQuery,
  useCreateInviteLinkMutation,
  useUpdateInviteLinkMutation,
  useDeleteInviteLinkMutation,
  useApproveUserMutation,
} from './contractorsApiSlice';
import { useGetUsersQuery } from '../users/usersApiSlice';
import { Icon, Loader } from 'semantic-ui-react';
import CustomDropdown from '../../components/CustomDropdown';
import { ui, Pill, Modal, fieldStyle, labelStyle } from '../../components/adminUi';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { shortDate } from '../chat_sessions/MobileChatSessionList';
import ContractorPersonasTab from './ContractorPersonasTab';
import { Dash, DetailPageShell, DetailTabs, ListNote, ListTable, RowAction, RowButton, Tag, cardAction, cardMeta, listCard, pillButton, stack, valueBoxStyle } from '../../components/listPage';

const ContractorDetail = () => {
  const { id } = useParams();
  const { isMobile } = useBreakpoint();
  const { data: contractor, isLoading } = useGetContractorQuery(id);
  const { data: adminsData } = useGetContractorAdminsQuery({ contractor: id });
  const { data: linksData } = useGetInviteLinksQuery({ contractor: id });
  const { data: usersData } = useGetUsersQuery({ contractor: id, is_approved: false });
  const { data: allUsersData } = useGetUsersQuery({});

  const [createContractorAdmin] = useCreateContractorAdminMutation();
  const [deleteContractorAdmin] = useDeleteContractorAdminMutation();
  const [createInviteLink] = useCreateInviteLinkMutation();
  const [updateInviteLink] = useUpdateInviteLinkMutation();
  const [deleteInviteLink] = useDeleteInviteLinkMutation();
  const [approveUser] = useApproveUserMutation();

  // ?tab=personas: the persona page's partner links land on that tab.
  const [searchParams] = useSearchParams();
  // Assignment is superuser-only (the endpoint 403s a 파트너 관리자), so is the tab.
  const isSuperuser = useSelector(selectCurrentUser)?.is_superuser === true;
  const [activeTab, setActiveTab] = useState(isSuperuser && searchParams.get('tab') === 'personas' ? 3 : 0);
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [adminUserId, setAdminUserId] = useState('');
  const [createLinkModal, setCreateLinkModal] = useState(false);
  const [linkFormData, setLinkFormData] = useState({ expires_at: '', max_uses: '' });
  const [confirmRemoveAdmin, setConfirmRemoveAdmin] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [confirmDeleteLink, setConfirmDeleteLink] = useState(null);

  const admins = Array.isArray(adminsData) ? adminsData : adminsData?.results || [];
  const links = Array.isArray(linksData) ? linksData : linksData?.results || [];
  const users = Array.isArray(usersData) ? usersData : usersData?.results || [];
  const allUsers = Array.isArray(allUsersData) ? allUsersData : allUsersData?.results || [];

  const adminUserIds = new Set(admins.map(a => a.user));
  const userOptions = allUsers
    .filter(u => !adminUserIds.has(u.id))
    .map(u => ({ value: u.id, text: `${u.full_name || u.username} (${u.email})` }));

  if (isLoading) return <Loader active inline="centered" />;
  if (!contractor) {
    return (
      <DetailPageShell wide isMobile={isMobile} title="파트너" backTo="/admin/contractors">
        <div style={{ ...listCard, textAlign: 'center', color: ui.muted }}>파트너를 찾을 수 없습니다.</div>
      </DetailPageShell>
    );
  }

  const handleAddAdmin = async () => {
    try {
      await createContractorAdmin({ user: adminUserId, contractor: id }).unwrap();
      setAddAdminModal(false);
      setAdminUserId('');
    } catch (err) {
      console.error('Failed to add admin:', err);
    }
  };

  const handleRemoveAdmin = async () => {
    if (confirmRemoveAdmin) {
      try {
        await deleteContractorAdmin(confirmRemoveAdmin.id).unwrap();
      } catch (err) {
        console.error('Failed to remove admin:', err);
      }
      setConfirmRemoveAdmin(null);
    }
  };

  const handleCreateLink = async () => {
    try {
      const data = { contractor: id };
      if (linkFormData.expires_at) data.expires_at = linkFormData.expires_at;
      if (linkFormData.max_uses) data.max_uses = parseInt(linkFormData.max_uses, 10);
      await createInviteLink(data).unwrap();
      setCreateLinkModal(false);
      setLinkFormData({ expires_at: '', max_uses: '' });
    } catch (err) {
      console.error('Failed to create invite link:', err);
    }
  };

  const handleToggleLink = async (link) => {
    try {
      await updateInviteLink({ id: link.id, is_active: !link.is_active }).unwrap();
    } catch (err) {
      console.error('Failed to toggle link:', err);
    }
  };

  const getInviteUrl = (token) => `${window.location.origin}/onboarding?token=${token}`;

  const handleCopy = (value, key) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(k => (k === key ? null : k)), 2000);
  };

  const handleDeleteLink = async () => {
    try {
      await deleteInviteLink(confirmDeleteLink.id).unwrap();
    } catch (err) {
      console.error('Failed to delete invite link:', err);
    }
    setConfirmDeleteLink(null);
  };

  const tabs = [
    { label: '승인 대기', count: users.length },
    { label: '관리자', count: admins.length },
    { label: '초대 링크', count: links.length },
    isSuperuser && { label: '고객 페르소나', count: contractor.persona_count },
  ].filter(Boolean);
  const name = u => u?.full_name || u?.username;

  // Only users still waiting (is_approved=false): one approved drops off this list, so revoking
  // access is 직원 관리's job, filtered here to this 파트너.
  const pending = (
    <div style={stack}>
      <ListNote>
        이 파트너로 가입을 신청하고 승인을 기다리는 사용자입니다. <b>승인</b>하면 로그인과 학습을 시작할 수 있습니다.
        이미 승인된 사용자의 접근을 막으려면 <Link to={`/userslist?page=1&page_size=${PAGE_SIZE}&contractor=${id}`} style={{ fontWeight: 600, color: 'inherit', textDecoration: 'underline' }}>{NAV.MY_STAFF}</Link>에서 승인을 취소하세요.
      </ListNote>
      <ListTable
        isMobile={isMobile}
        rows={users}
        empty="승인 대기 중인 사용자가 없습니다."
        columns={[
          { key: 'name', label: '이름', nowrap: true, render: u => <span style={{ fontWeight: 600 }}>{name(u)}</span> },
          { key: 'email', label: '이메일', render: u => <span style={{ color: ui.muted }}>{u.email}</span> },
          { key: 'created', label: '가입일', muted: true, render: u => shortDate(u.created_at) },
          { key: 'actions', label: '', align: 'right', render: u => <RowButton primary onClick={() => approveUser(u.id)}>승인</RowButton> },
        ]}
        card={{
          title: name,
          meta: u => [u.email, `가입 ${shortDate(u.created_at)}`],
          actions: u => [<button key="a" type="button" style={cardAction(true, true)} onClick={() => approveUser(u.id)}>승인</button>],
        }}
      />
    </div>
  );

  const adminsTab = (
    <div style={stack}>
      <ListNote>
        파트너 관리자는 해당 파트너 훈련생의 학습·평가 데이터를 조회할 수 있습니다. <b>관리자 추가</b>로 사용자를 지정하고, 휴지통 아이콘으로 권한을 회수합니다.
      </ListNote>
      <div>
        <button type="button" onClick={() => setAddAdminModal(true)} style={pillButton(true, true)}>
          관리자 추가 <Icon name="plus" className="centered-icon" />
        </button>
      </div>
      <ListTable
        isMobile={isMobile}
        rows={admins}
        empty="지정된 관리자가 없습니다."
        columns={[
          { key: 'name', label: '이름', nowrap: true, render: a => <span style={{ fontWeight: 600 }}>{name(a.user_detail)}</span> },
          { key: 'email', label: '이메일', render: a => <span style={{ color: ui.muted }}>{a.user_detail?.email}</span> },
          { key: 'by', label: '배정자', render: a => name(a.assigned_by_detail) || <Dash /> },
          { key: 'at', label: '배정일', muted: true, render: a => shortDate(a.assigned_at) },
          { key: 'actions', label: '', align: 'right', render: a => <RowAction icon="trash alternate outline" title="관리자 제거" onClick={() => setConfirmRemoveAdmin(a)} /> },
        ]}
        card={{
          title: a => name(a.user_detail),
          meta: a => [a.user_detail?.email, `배정 ${shortDate(a.assigned_at)}`],
          actions: a => [<button key="d" type="button" style={cardAction(false, true)} onClick={() => setConfirmRemoveAdmin(a)}>관리자 제거</button>],
        }}
      />
    </div>
  );

  // Why a link is dead, not just that it is: an expired link used to read "비활성" while its
  // button still offered 비활성화.
  const linkStatus = l => {
    if (!l.is_active) return <Tag tone="gray">비활성</Tag>;
    if (l.expires_at && new Date(l.expires_at) < new Date()) return <Tag tone="amber">만료됨</Tag>;
    if (l.max_uses && l.use_count >= l.max_uses) return <Tag tone="amber">사용 완료</Tag>;
    return <Tag tone="green">활성</Tag>;
  };
  const fullDate = d => new Date(d).toLocaleDateString('ko-KR');
  const linkMeta = l => [
    `사용 ${l.use_count}${l.max_uses ? ` / ${l.max_uses}` : ''}회`,
    l.expires_at ? `만료 ${fullDate(l.expires_at)}` : '만료 없음',
    `생성 ${fullDate(l.created_at)}${l.created_by_detail ? ` · ${name(l.created_by_detail)}` : ''}`,
  ].join(' · ');
  // The whole value, copyable on its own: the link for sharing, the token for the 온보딩
  // page's "초대 토큰" field (someone who opened the site without the link).
  const copyRow = (label, value, key) => (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr) auto' : '72px minmax(0, 1fr) auto', gap: isMobile ? '6px 8px' : '12px', alignItems: 'center' }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#86868b', gridColumn: isMobile ? '1 / -1' : undefined }}>{label}</div>
      {/* <code>: index.css forces the sans font on div/span with !important, and a typed token
          needs I/l/1 and O/0 told apart. */}
      <div style={{ ...valueBoxStyle, height: 'auto', minHeight: '42px', wordBreak: 'break-all' }}><code style={{ fontSize: '13px', color: ui.text, lineHeight: 1.5 }}>{value}</code></div>
      <RowButton primary={copiedKey === key} icon={copiedKey === key ? 'check' : 'copy outline'} onClick={() => handleCopy(value, key)}>{copiedKey === key ? '복사됨' : '복사'}</RowButton>
    </div>
  );
  const linksTab = (
    <div style={stack}>
      <ListNote>
        이 링크로 가입한 사용자는 자동으로 이 파트너에 소속됩니다. <b>링크</b>를 복사해 배포하세요. 링크 없이 접속한 사용자는 가입 화면에 <b>토큰</b>을 입력하면 됩니다.
      </ListNote>
      <div>
        <button type="button" onClick={() => setCreateLinkModal(true)} style={pillButton(true, true)}>
          새 초대 링크 <Icon name="plus" className="centered-icon" />
        </button>
      </div>
      {links.length === 0 && <div style={{ ...listCard, textAlign: 'center', color: ui.muted }}>생성된 초대 링크가 없습니다.</div>}
      {links.map(l => (
        <div key={l.id} style={{ ...listCard, ...stack }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {linkStatus(l)}
            <span style={{ ...cardMeta, marginTop: 0, flex: 1, minWidth: 0, whiteSpace: 'normal' }}>{linkMeta(l)}</span>
            <div style={{ display: 'inline-flex', gap: '4px', marginLeft: 'auto' }}>
              <RowButton primary={!l.is_active} onClick={() => handleToggleLink(l)}>{l.is_active ? '비활성화' : '활성화'}</RowButton>
              <RowAction icon="trash alternate outline" title="링크 삭제" onClick={() => setConfirmDeleteLink(l)} />
            </div>
          </div>
          {copyRow('초대 링크', getInviteUrl(l.token), `${l.id}:url`)}
          {copyRow('토큰', l.token, `${l.id}:token`)}
        </div>
      ))}
    </div>
  );

  return (
    <DetailPageShell wide isMobile={isMobile} title={contractor.name} subtitle={`생성 ${shortDate(contractor.created_at)}`} backTo="/admin/contractors">
      <DetailTabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
      {activeTab === 3
        ? <ContractorPersonasTab contractorId={id} isMobile={isMobile} />
        : [pending, adminsTab, linksTab][activeTab]}

      {/* Add Admin Modal */}
      <Modal
        open={addAdminModal}
        onClose={() => setAddAdminModal(false)}
        title="파트너 관리자 추가"
        footer={
          <>
            <Pill variant="ghost" onClick={() => setAddAdminModal(false)}>취소</Pill>
            <Pill variant="primary" onClick={handleAddAdmin} disabled={!adminUserId}>추가</Pill>
          </>
        }
      >
        <label style={labelStyle}>사용자 선택</label>
        <CustomDropdown searchable placeholder="사용자를 선택하세요" options={userOptions.map(o => ({ key: o.value, ...o }))} value={adminUserId} onChange={v => setAdminUserId(v || '')} icon="user" style={{ width: '100%' }} />
      </Modal>

      {/* Create Invite Link Modal */}
      <Modal
        open={createLinkModal}
        onClose={() => setCreateLinkModal(false)}
        title="새 초대 링크 생성"
        footer={
          <>
            <Pill variant="ghost" onClick={() => setCreateLinkModal(false)}>취소</Pill>
            <Pill variant="primary" onClick={handleCreateLink}>생성</Pill>
          </>
        }
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>만료일시 (선택)</label>
          <input
            type="datetime-local"
            style={fieldStyle}
            value={linkFormData.expires_at}
            onChange={e => setLinkFormData(prev => ({ ...prev, expires_at: e.target.value }))}
          />
        </div>
        <div>
          <label style={labelStyle}>최대 사용 횟수 (선택)</label>
          <input
            type="number"
            min="1"
            placeholder="무제한"
            style={fieldStyle}
            value={linkFormData.max_uses}
            onChange={e => setLinkFormData(prev => ({ ...prev, max_uses: e.target.value }))}
          />
        </div>
      </Modal>

      <Modal
        open={!!confirmDeleteLink}
        onClose={() => setConfirmDeleteLink(null)}
        title="초대 링크 삭제"
        width={400}
        footer={
          <>
            <Pill variant="ghost" onClick={() => setConfirmDeleteLink(null)}>취소</Pill>
            <Pill variant="danger" onClick={handleDeleteLink}>삭제</Pill>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: '14px', color: ui.text, lineHeight: 1.6 }}>
          이 링크와 토큰은 즉시 사용할 수 없게 됩니다. 이미 가입한 사용자에게는 영향이 없습니다.
        </p>
      </Modal>

      {/* Confirm Remove Admin */}
      <Modal
        open={!!confirmRemoveAdmin}
        onClose={() => setConfirmRemoveAdmin(null)}
        title="관리자 제거"
        width={400}
        footer={
          <>
            <Pill variant="ghost" onClick={() => setConfirmRemoveAdmin(null)}>취소</Pill>
            <Pill variant="danger" onClick={handleRemoveAdmin}>제거</Pill>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: '14px', color: ui.text, lineHeight: 1.6 }}>
          <b>{confirmRemoveAdmin?.user_detail?.full_name || confirmRemoveAdmin?.user_detail?.username}</b> 님의 관리자 권한을 제거하시겠습니까?
        </p>
      </Modal>
    </DetailPageShell>
  );
};

export default ContractorDetail;
